<template>
  <div class="filters">
    <div class="filters__box">
      <header
        :class="displayKpiList ? 'filters__box__header filters__box__header--padding-left' : 'filters__box__header'"
      >
        <ui-button
          class="filters__box__header__close"
          @click.prevent="onClickToggleFilters()"
          button="secondary"
          :label="$t('commons.buttons.close')"
          :icon-only="true"
          icon="close"
        />
        <div v-if="dataSettings.length <= 1" class="filters__box__header__single">
          <div class="filters__box__header__single__title">
            {{ $t('filters.title.select') }}
          </div>
        </div>
        <div
          v-if="dataSettings.length > 1"
          class="filters__box__header__list"
          :class="displayKpiList ? 'filters__box__header__list--reverse' : false"
          @click="displayKpiList = !displayKpiList"
        >
          <div class="filters__box__header__list__title">
            {{ $t('filters.title.list') }}
          </div>
          <div class="filters__box__header__list__chevron backoffice-icons">
            {{ displayKpiList ? 'chevron_left' : 'chevron_right' }}
          </div>
        </div>
      </header>
      <div class="filters__box__container">
        <transition key="if" name="slide-left" mode="out-in">
          <div v-if="!displayKpiList" class="filters__box__container__select">
            <div class="filters__box__container__select__section">
              <transition-group name="expand-height">
                <div key="1" class="filters__box__container__select__section__header">
                  <div class="filters__box__container__select__section__header__content">
                    <div class="filters__box__container__select__section__header__content__title">
                      {{ activeSetting.name[$i18n.locale] || activeSetting.name.default }}
                    </div>
                    <div
                      class="filters__box__container__select__section__header__content__description"
                      v-if="activeSetting.description"
                    >
                      {{ activeSetting.description[$i18n.locale] || activeSetting.description.default }}
                    </div>
                  </div>

                  <div class="filters__box__container__select__section__header__export">
                    <ui-button
                      @click="onClickExportButton"
                      v-tooltip="{
                        placement: 'top',
                        trigger: 'hover',
                        content: $t('settings.export.button'),
                        offset: 3,
                      }"
                      class="filters__box__container__select__section__header__export__button"
                      :label="$t('settings.export.button')"
                      icon="download"
                      :icon-only="true"
                      button="secondary"
                      :is-link="true"
                      :href="exportLink"
                    />
                  </div>
                </div>
                <div
                  key="2"
                  class="filters__box__container__select__section__content"
                  v-if="activeKpi === `${activeSetting.key}`"
                >
                  <filter-brand
                    class="filters__box__container__select__section__content__sub-filters"
                    :brandsObj="dataBrands[activeSetting.key]"
                    :active-brands="activeBrands"
                    :active-groups="activeGroups"
                    :all-brands="allBrands"
                    :all-groups-brands="allGroupsBrands"
                    :colors="filterColors"
                    :with-distribution="activeSetting.with_location_distribution"
                    :is-zone="$route.name === 'zones' || $route.name === 'divisionZones'"
                    :is-analytics="$route.name === 'analytics'"
                    :settings="settings"
                    @updateBrands="updateBrands"
                    @updateGroups="updateGroups"
                    @toggle="toggleBrands"
                    @toggleGroups="toggleGroups"
                    v-if="!isIntentionView"
                  />
                  <filter-filter
                    class="filters__box__container__select__section__content__sub-filters"
                    :filterObj="dataFilters[activeSetting.key]"
                    :active-filters="activeFilters"
                    v-if="activeSetting.with_filter && $route.name !== 'zones' && $route.name !== 'analytics'"
                    @update="updateFilters"
                  />
                  <filter-distribution
                    class="filters__box__container__select__section__content__sub-filters"
                    :distributionObj="dataDistributions[activeSetting.key]"
                    :current-distribution="currentDistribution"
                    :is-active="filters.distribution"
                    v-if="
                      activeSetting.with_distribution &&
                        $route.name !== 'zones' &&
                        $route.name !== 'analytics' &&
                        dataDistributions[activeSetting.key] &&
                        dataDistributions[activeSetting.key].max > 0 &&
                        currentDistribution
                    "
                    @update="updateDistribution"
                    @refresh="$emit('refreshLocations')"
                    @toggle="toggleDistribution"
                  />
                  <template
                    v-if="
                      activeSetting.with_source &&
                        dataSources[activeSetting.key] &&
                        dataSources[activeSetting.key].sources.length > 0
                    "
                  >
                    <filter-source
                      v-if="!isMarkersIntentionView"
                      class="filters__box__container__select__section__content__sub-filters"
                      :sourceObj="dataSources[activeSetting.key]"
                      :current-source="currentSource"
                      :is-intention-view="isIntentionView"
                      @update="updateSource"
                    />
                  </template>
                  <filter-logo
                    class="filters__box__container__select__section__content__sub-filters"
                    :src="activeSetting.logo"
                    :link="activeSetting.logo_url"
                    v-if="activeSetting.logo"
                  />
                </div>
              </transition-group>
            </div>
          </div>

          <div key="else" class="filters__box__container__list" v-else>
            <div class="filters__box__container__list__section" v-for="setting in dataSettings" :key="`${setting.key}`">
              <filter-kpi
                class="filters__box__container__list__section__kpi"
                :buttonIsActive="false"
                :label="setting.name[$i18n.locale] || setting.name.default"
                :is-active="setting.key === currentKpi"
                v-if="dataSettings.length > 1"
                @click.native="updateKpi(setting.key)"
              />
            </div>
            <filter-logo
              class="filters__box__container__select__section__content__sub-filters"
              :src="activeSetting.logo"
              :link="activeSetting.logo_url"
              v-if="activeSetting.logo"
            />
          </div>
        </transition>
      </div>
      <footer class="filters__box__footer">
        <ui-button
          class="filters__box__footer__close"
          :label="$t('commons.buttons.close')"
          button="primary"
          @click.prevent="toggleFilters(false)"
        />
      </footer>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import UiButton from '@/components/UI/Button.vue'
import FilterBrand from '@/components/Filter/Brand.vue'
import FilterFilter from '@/components/Filter/Filter.vue'
import FilterDistribution from '@/components/Filter/Distribution.vue'
import FilterSource from '@/components/Filter/Source.vue'
import FilterKpi from '@/components/Filter/Kpi.vue'
import FilterLogo from '@/components/Filter/Logo.vue'
import { isMobile } from '@/utils/navigator.util'
import { launchGAEvent } from '@/utils/gtm.util'
import { getExportLink } from '@/services/radar.service'
import {
  buildFilters,
  buildSource,
  buildDistribution,
  buildGroups,
  buildDate,
  buildBrands,
  buildKpi,
  buildLocation,
  buildDivision,
  buildCountries,
  buildLocale,
  buildView,
} from '@/utils/urlParams.util'

export default {
  name: 'Filters',
  components: {
    UiButton,
    FilterBrand,
    FilterFilter,
    FilterDistribution,
    FilterSource,
    FilterKpi,
    FilterLogo,
  },
  data() {
    return {
      isMobile: isMobile(),
      displayKpiList: false,
    }
  },
  computed: {
    ...mapState({
      dataSettings: state => state.data.settings,
      dataBrands: state => state.data.brands,
      dataFilters: state => state.data.filters,
      dataDistributions: state => state.data.distributions,
      dataSources: state => state.data.sources,

      algoliaSources: state => state.algolia.sources,

      activeBrands: state => state.refine.activeBrands,
      activeGroups: state => state.refine.activeGroups,
      activeFilters: state => state.refine.activeFilters,
      allBrands: state => state.refine.allBrands,
      allGroupsBrands: state => state.refine.allGroupsBrands,
      currentSource: state => state.refine.currentSource,
      currentDistribution: state => state.refine.currentDistribution,
      currentKpi: state => state.refine.currentKpi,
      currentCountries: state => state.refine.currentCountries,
      currentDistance: state => state.refine.currentDistance,
      currentTimeline: state => state.refine.currentTimeline,

      brandsGroup: state => state.data.brandsGroup,
      groupTabIsActive: state => state.radar.filters.group,
      settings: state => state.radar.settings,
      filters: state => state.radar.filters,
    }),
    ...mapGetters({
      isIntentionView: 'radar/isIntentionView',
      isMarkersIntentionView: 'radar/isMarkersIntentionView',
    }),
    activeKpi() {
      return this.currentKpi
    },
    filterColors() {
      return this.dataFilters[this.currentKpi].filters.map(filter => filter.color)
    },
    activeSetting() {
      return this.dataSettings.find(setting => setting.key === this.currentKpi)
    },
    exportLink() {
      const params = this.$route.params
      const query = this.$route.query
      let urlParams = '&format=csv'
      urlParams += buildDivision(params.level, params.key)
      urlParams += buildLocation(params.location, this.currentDistance)
      urlParams += buildSource(this.currentSource)
      urlParams += buildDistribution(this.currentDistribution, this.filters.distribution)
      urlParams += buildKpi(this.currentKpi)
      urlParams += buildGroups(this.activeGroups)
      urlParams += buildBrands(this.activeBrands, this.dataBrands[this.currentKpi].brands, this.activeGroups)
      urlParams += buildFilters(this.activeFilters)
      urlParams += buildCountries(this.currentCountries)
      urlParams += buildView(query.view)
      urlParams += buildLocale(query.locale)
      urlParams += buildDate(this.currentTimeline)

      return getExportLink(params.id, urlParams, this.$route.name)
    },
  },
  methods: {
    ...mapActions({
      toggleFilters: 'radar/toggleFilters',
      toggleFiltersDistribution: 'radar/toggleFiltersDistribution',

      setCurrentLocation: 'data/setCurrentLocation',

      initActiveFilters: 'refine/initActiveFilters',
      initCurrentSource: 'refine/initCurrentSource',
      updateCurrentKpi: 'refine/updateCurrentKpi',
      updateActiveFilters: 'refine/updateActiveFilters',
      updateCurrentSource: 'refine/updateCurrentSource',
      updateActiveBrands: 'refine/updateActiveBrands',
      updateActiveGroups: 'refine/updateActiveGroups',
      toggleAllBrands: 'refine/toggleAllBrands',
      toggleAllGroupsBrands: 'refine/toggleAllGroupsBrands',
      updateCurrentDistribution: 'refine/updateCurrentDistribution',
    }),
    onClickExportButton() {
      launchGAEvent(this.$route.params.id, `${this.$route.name}_settings`, 'export_all_data')
    },
    updateBrands(summary) {
      launchGAEvent(this.$route.params.id, `${this.$route.name}_brand_filter`, 'clicks')
      this.updateActiveBrands(summary)
      this.$emit('refreshFilters')
    },
    updateGroups(summary) {
      this.updateActiveGroups(summary)
    },
    toggleBrands(bool) {
      this.toggleAllBrands({ bool, brands: this.dataBrands[this.currentKpi].brands })
      this.$emit('refreshFilters')
    },
    toggleGroups(groupTabIsActive) {
      this.toggleAllGroupsBrands({ groupTabIsActive, brands: this.brandsGroup })
      this.$emit('refreshFilters')
    },
    updateFilters(filters) {
      launchGAEvent(this.$route.params.id, 'markers_grade_filter', 'clicks')
      this.updateActiveFilters(filters)
      this.$emit(this.isMarkersIntentionView ? 'refreshAlgoliaFilters' : 'refreshFilters')
    },
    updateSource(source) {
      launchGAEvent(this.$route.params.id, `${this.$route.name}_source_filter`, 'clicks')
      this.updateCurrentSource(source)
      this.$emit('refreshFilters')
    },
    updateDistribution(distribution) {
      launchGAEvent(this.$route.params.id, 'markers_distribution', 'slide')
      this.updateCurrentDistribution(distribution)
    },
    toggleDistribution(bool) {
      launchGAEvent(this.$route.params.id, 'markers_distribution', 'toggle')
      this.toggleFiltersDistribution(bool)
      this.$emit('refreshLocations')
    },
    async updateKpi(newKpi) {
      if (newKpi !== this.currentKpi) {
        this.displayKpiList = false
        await this.updateCurrentKpi(newKpi)
        const sources = this.dataSources[this.currentKpi] ? this.dataSources[this.currentKpi].sources : []
        const filters = this.dataFilters[this.currentKpi] ? this.dataFilters[this.currentKpi].filters : []
        await this.initCurrentSource({ sources })
        await this.initActiveFilters({ filters })
        this.$emit('refreshFilters')
      }
    },
    onClickToggleFilters() {
      launchGAEvent(this.$route.params.id, 'analytics_filter', 'hide')
      this.toggleFilters(false)
    },
  },
}
</script>

<style lang="scss" scoped>
.filters {
  @include wrapper(flex-end, $gutter-md $gutter-md calc(#{$gutter-md} * 2.5 + #{$button-min-height-desktop}));

  @media (min-width: $screen-sm) {
    left: initial;
    width: calc(#{$gmap-sidebar-width-tablet} + #{$gutter-md} * 2);
  }

  @media (min-width: $screen-lg) {
    width: calc(#{$gmap-sidebar-width-desktop} + #{$gutter-md} * 2);
  }

  &__box {
    @include box;

    width: $gmap-sidebar-width-mobile;

    @media (min-width: $screen-sm) {
      width: $gmap-sidebar-width-tablet;
      height: 100%;
    }

    @media (min-width: $screen-lg) {
      width: $gmap-sidebar-width-desktop;
    }

    &__header {
      @include header;
      transition: padding $transition-duration-default $transition-effect-default;
      border-bottom: none;

      &__list {
        transition: color $transition-duration-medium $transition-effect-default;
        display: flex;
        align-items: center;
        cursor: pointer;
        &:hover {
          color: map-get($generic-color-variants, 'gtr');
        }

        &__title {
          @include title;
        }
        &--reverse {
          flex-direction: row-reverse;
        }
      }

      &__single {
        &__title {
          @include title;
        }
      }

      &--padding-left {
        padding-left: $spacing-xs;

        @media (min-width: $screen-sm) {
          padding-left: $spacing-xs;
        }
      }

      &__close {
        @include close;
      }
    }

    &__container {
      @include container(0, 0);
      &__select {
        &__section {
          display: flex;
          flex-direction: column;

          &__kpi {
            margin: 0 (-$spacing-sm);
          }

          &__content {
            padding: 0 $spacing-sm $spacing-sm $spacing-sm;

            &__description,
            &__sub-filters {
              margin-top: $spacing-sm;

              @media (min-width: $screen-sm) {
                margin-top: $spacing-md;
              }
            }

            &__sub-filters {
              &:last-child {
                margin-bottom: $spacing-sm;

                @media (min-width: $screen-sm) {
                  margin-bottom: $spacing-md;
                }
              }

              &__title {
                @include sub-title;
              }

              &__checkbox {
                margin-bottom: $spacing-sm / 2;
                width: 100%;

                &:last-child {
                  margin-bottom: 0;
                }

                &__distribution {
                  display: flex;
                  margin-top: 2px;

                  &__item {
                    margin-left: -1px;
                    border-radius: 25px 0 0 25px;
                    height: 6px;

                    &:last-child {
                      border-radius: 25px;
                    }
                  }
                }

                &__label {
                  display: flex;
                  justify-content: space-between;

                  &__count {
                    text-align: right;
                    font-weight: bold;
                  }
                }
              }
            }
          }

          &__header {
            background-color: $grey-athens-light;
            padding: $spacing-sm;
            display: flex;
            flex-direction: row;
            align-items: center;

            &__content {
              &__title {
                @include title;
              }

              &__description {
                margin-top: $spacing-xs;
              }
            }

            &__export {
              margin: 3px 0;
              padding: 0 4px;

              &__button {
                border: 0;
                &:hover:not(:disabled) {
                  border: 0;
                  background-color: unset;
                  color: $blue-puerto-rico;
                }

                &:focus:not(:disabled) {
                  border: 0;
                  background-color: unset;
                  color: $blue-puerto-rico;
                }
              }
            }
          }
        }
      }

      &__list {
        padding: 0 $spacing-sm $spacing-sm $spacing-sm;

        &__section {
          display: flex;
          flex-direction: column;
          margin: 0 $spacing-xs;

          &__kpi {
            margin: 0 (-$spacing-sm);
          }
        }
      }
    }

    &__footer {
      @include footer;

      @media (min-width: $screen-sm) {
        display: none;
      }
    }
  }

  &__list-kpi {
    @include box;

    width: $gmap-sidebar-width-mobile;
  }
}
</style>
